import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import { api } from '../../api/client';
import { ApiError, errorMessageKey, unwrap } from '../../api/api-error';
import { ME_KEY } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { AuthError, AuthLayout, AuthLinks } from '../../components/auth/auth-dialog';
import { TextField } from '../../components/ui/text-field';
import { useI18n } from '../../i18n/i18n';

const schema = z.object({ code: z.string().trim().min(6, 'validation.code') });
type Values = z.infer<typeof schema>;

/** Второй шаг входа: код из приложения или код восстановления (ADR-0003). */
export function SecondFactorPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { code: '' } });

  const verify = useMutation({
    mutationFn: async (values: Values) =>
      unwrap(await api.POST('/api/v1/auth/login/second-factor', { body: values })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ME_KEY });
      const from = (location.state as { from?: string } | null)?.from;
      await navigate(from ?? '/', { replace: true });
    },
  });
  const expired =
    verify.error instanceof ApiError && verify.error.code === 'AUTH_SECOND_FACTOR_EXPIRED';

  return (
    <AuthLayout title={t('auth.secondFactor.title')}>
      <p className="text-sm text-muted-foreground">{t('auth.secondFactor.hint')}</p>
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit((values) => verify.mutate(values))}
        noValidate
      >
        <TextField
          control={form.control}
          name="code"
          label={t('auth.field.code')}
          autoComplete="one-time-code"
          autoFocus
        />
        {verify.isError && <AuthError>{t(errorMessageKey(verify.error))}</AuthError>}
        <Button type="submit" className="w-full" disabled={verify.isPending}>
          {t('auth.secondFactor.submit')}
        </Button>
      </form>
      {expired && (
        <AuthLinks>
          <Link to="/login">{t('auth.secondFactor.restart')}</Link>
        </AuthLinks>
      )}
    </AuthLayout>
  );
}
